import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Input } from './ui/Input';
import { Select } from './ui/Select';
import { ColorPicker } from './ui/ColorPicker';
import { Button } from './ui/Button';
import { Toggle } from './ui/Toggle';
import { Alert } from './ui/Alert';
import { toast } from 'sonner';
import { apiFetch, parseJsonResponse, isNetworkErrorResponse, getImageUrl } from '../utils/api';
import { SmartBannerItemEditor } from './SmartBannerItemEditor';

interface SmartBannerFormProps {
  banner: any | null;
  establishmentId: string;
  onSuccess: () => void;
  onCancel: () => void;
}

interface Font {
  id: number;
  name: string;
  display_name: string;
  font_family: string;
}

interface Product {
  id: number;
  name: string;
  price: number;
  referencia: string | null;
  image_url?: string | null;
}

interface ProductReference {
  reference_code: string;
  base_name: string;
  family?: string | null;
  variants: Array<{
    product_id: number;
    name: string;
    price: number;
    image_url?: string | null;
  }>;
}

interface Screen {
  id: number;
  name: string;
  establishment_id: number;
  banner_divisions?: number;
  orientation?: 'horizontal' | 'vertical';
}

interface Comment {
  id: number;
  cod_comentario: string;
  comentario: string;
  familia: string | null;
  articulo: string | null;
  is_active?: number;
}

interface SmartBannerItem {
  id?: number;
  item_type: 'product' | 'custom' | 'extra';
  product_id?: number | null;
  comment_id?: number | null;
  custom_name?: string | null;
  custom_price?: number | null;
  custom_image_path?: string | null;
  show_image: number;
  show_price: number;
  display_order: number;
  group_name?: string | null;
}

interface SmartBannerScreen {
  pantalla_id: number;
  position: 'superior' | 'lateral' | 'inferior';
  lateral_position?: number | null;
  display_order: number;
}

export const SmartBannerForm: React.FC<SmartBannerFormProps> = ({
  banner,
  establishmentId,
  onSuccess,
  onCancel
}) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'basic' | 'style' | 'items' | 'screens'>('basic');
  
  // Catálogos
  const [fonts, setFonts] = useState<Font[]>([]);
  const [availableReferences, setAvailableReferences] = useState<ProductReference[]>([]);
  const [availableComments, setAvailableComments] = useState<Comment[]>([]);
  const [screens, setScreens] = useState<Screen[]>([]);
  const [loadingCatalogs, setLoadingCatalogs] = useState(true);

  // Form data
  const [formData, setFormData] = useState({
    name: '',
    banner_type: 'extras' as 'extras' | 'bebidas_postres' | 'chips' | 'freebies' | 'combo' | 'postres',
    background_type: 'color' as 'color' | 'image',
    background_color: '#ffffff',
    background_image_path: null as string | null,
    header_type: 'text' as 'image' | 'text',
    header_image_path: null as string | null,
    header_text: '',
    header_font_id: null as number | null,
    header_font_size: 24,
    header_text_color: '#000000',
    header_text_align: 'center' as 'left' | 'center' | 'right',
    item_text_font_size: 14,
    item_price_font_size: 16,
    item_text_color: '#000000',
    item_price_color: '#000000',
    item_text_font_id: null as number | null,
    item_price_font_id: null as number | null,
    columns: 1,
    is_active: 1,
    display_order: 0
  });

  const [items, setItems] = useState<SmartBannerItem[]>([]);
  const [selectedScreens, setSelectedScreens] = useState<SmartBannerScreen[]>([]);
  
  // Archivos para subir
  const [backgroundImageFile, setBackgroundImageFile] = useState<File | null>(null);
  const [backgroundImagePreview, setBackgroundImagePreview] = useState<string | null>(null);
  const [headerImageFile, setHeaderImageFile] = useState<File | null>(null);
  const [headerImagePreview, setHeaderImagePreview] = useState<string | null>(null);

  // Cargar catálogos
  useEffect(() => {
    const loadCatalogs = async () => {
      if (!token || !establishmentId) {
        setLoadingCatalogs(false);
        return;
      }

      setLoadingCatalogs(true);
      try {
        // Cargar fuentes
        const fontsRes = await apiFetch('catalog/fonts', {}, token);
        if (!isNetworkErrorResponse(fontsRes)) {
          const fontsJson = await parseJsonResponse(fontsRes);
          if (fontsJson.data?.fonts) {
            setFonts(fontsJson.data.fonts);
          }
        }

        // Cargar productos disponibles
        const productsParams = new URLSearchParams({
          establishment_id: establishmentId
        });
        const productsRes = await apiFetch(`screens/available-products&${productsParams}`, {}, token);
        if (!isNetworkErrorResponse(productsRes)) {
          const productsJson = await parseJsonResponse(productsRes);
          const responseData = productsJson.data || productsJson;
          const references = (responseData?.references || responseData?.products || []).map(
            (ref: ProductReference) => ({
              ...ref,
              variants: ref.variants || []
            })
          );
          setAvailableReferences(references);
        }

        // Cargar comentarios (extras) disponibles
        const commentsRes = await apiFetch('comments/list', {}, token);
        if (!isNetworkErrorResponse(commentsRes)) {
          const commentsJson = await parseJsonResponse(commentsRes);
          const responseData = commentsJson.data || commentsJson;
          const comments = (responseData?.comments || []).filter((c: Comment) => c.is_active !== 0);
          setAvailableComments(comments);
        }

        // Cargar pantallas del establecimiento
        const screensParams = new URLSearchParams({
          establishment_id: establishmentId
        });
        const screensRes = await apiFetch(`screens/list&${screensParams}`, {}, token);
        if (!isNetworkErrorResponse(screensRes)) {
          const screensJson = await parseJsonResponse(screensRes);
          if (screensJson.data?.screens) {
            setScreens(screensJson.data.screens);
          }
        }
      } catch (err: any) {
        console.error('Error cargando catálogos:', err);
        toast.error('Error al cargar catálogos');
      } finally {
        setLoadingCatalogs(false);
      }
    };

    loadCatalogs();
  }, [token, establishmentId]);

  // Cargar datos del banner si está editando
  useEffect(() => {
    if (banner) {
      setFormData({
        name: banner.name || '',
        banner_type: banner.banner_type || 'extras',
        background_type: banner.background_type || 'color',
        background_color: banner.background_color || '#ffffff',
        background_image_path: banner.background_image_path || null,
        header_type: banner.header_type || 'text',
        header_image_path: banner.header_image_path || null,
        header_text: banner.header_text || '',
        header_font_id: banner.header_font_id || null,
        header_font_size: banner.header_font_size || 24,
        header_text_color: banner.header_text_color || '#000000',
        header_text_align: banner.header_text_align || 'center',
        item_text_font_size: banner.item_text_font_size || 14,
        item_price_font_size: banner.item_price_font_size || 16,
        item_text_color: banner.item_text_color || '#000000',
        item_price_color: banner.item_price_color || '#000000',
        item_text_font_id: banner.item_text_font_id || null,
        item_price_font_id: banner.item_price_font_id || null,
        columns: banner.columns || 1,
        is_active: banner.is_active ?? 1,
        display_order: banner.display_order || 0
      });

      if (banner.items) {
        setItems(banner.items.map((item: any, index: number) => ({
          id: item.id,
          item_type: item.item_type || 'custom',
          product_id: item.product_id || null,
          comment_id: item.comment_id || null,
          custom_name: item.custom_name || null,
          custom_price: item.custom_price || null,
          custom_image_path: item.custom_image_path || null,
          show_image: item.show_image ?? 1,
          show_price: item.show_price ?? 1,
          display_order: item.display_order ?? index,
          group_name: item.group_name || null
        })));
      }

      if (banner.screens) {
        setSelectedScreens(banner.screens.map((screen: any, index: number) => ({
          pantalla_id: screen.pantalla_id,
          position: screen.position || 'superior',
          lateral_position: screen.lateral_position || null,
          display_order: screen.display_order ?? index
        })));
      }

      // Previews de imágenes
      if (banner.background_image_path) {
        setBackgroundImagePreview(getImageUrl(banner.background_image_path) || null);
      }
      if (banner.header_image_path) {
        setHeaderImagePreview(getImageUrl(banner.header_image_path) || null);
      }
    }
  }, [banner]);

  const uploadImage = async (file: File, type: 'background' | 'header'): Promise<string | null> => {
    if (!file || !token) return null;

    setUploading(true);
    try {
      const formData = new FormData();
      const fieldName = type === 'background' ? 'background_image' : 'header_image';
      formData.append(fieldName, file);
      formData.append('image_type', type);

      const API_BASE_URL = process.env.NODE_ENV === 'production' ? '/api' : 'http://hiomenu.local/api';
      const url = `${API_BASE_URL}.php?route=smart-banners/upload-image`;
      
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!res.ok) {
        const text = await res.text();
        let json;
        try {
          json = JSON.parse(text);
        } catch (e) {
          throw new Error(`Error del servidor (${res.status}): ${text.substring(0, 200)}`);
        }
        const errorMessage = typeof json.error === 'string' ? json.error : json.message || 'Error al subir imagen';
        throw new Error(errorMessage);
      }

      const text = await res.text();
      let json;
      try {
        json = JSON.parse(text);
      } catch (e) {
        throw new Error('Respuesta inválida del servidor');
      }
      
      const imagePath = json.data?.image_path || json.image_path || null;
      return imagePath;
    } catch (err: any) {
      console.error('Error subiendo imagen:', err);
      toast.error(err.message || 'Error al subir imagen');
      throw err;
    } finally {
      setUploading(false);
    }
  };

  const handleBackgroundImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Tipo de archivo no permitido. Formatos permitidos: JPEG, PNG, GIF, WEBP');
      return;
    }

    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      toast.error('El archivo excede el tamaño máximo de 5MB');
      return;
    }

    setBackgroundImageFile(file);
    
    const reader = new FileReader();
    reader.onloadend = () => {
      setBackgroundImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleHeaderImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Tipo de archivo no permitido. Formatos permitidos: JPEG, PNG, GIF, WEBP');
      return;
    }

    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      toast.error('El archivo excede el tamaño máximo de 5MB');
      return;
    }

    setHeaderImageFile(file);
    
    const reader = new FileReader();
    reader.onloadend = () => {
      setHeaderImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !establishmentId) return;

    try {
      setLoading(true);
      setError(null);

      // Validaciones
      if (!formData.name.trim()) {
        setError('El nombre del banner es requerido');
        return;
      }

      if (formData.background_type === 'image' && !formData.background_image_path && !backgroundImageFile) {
        setError('Debes subir una imagen de fondo o seleccionar una existente');
        return;
      }

      if (formData.header_type === 'text' && !formData.header_text.trim()) {
        setError('El texto del encabezado es requerido cuando el tipo es texto');
        return;
      }

      if (formData.header_type === 'image' && !formData.header_image_path && !headerImageFile) {
        setError('Debes subir una imagen de encabezado o seleccionar una existente');
        return;
      }

      // Subir imágenes si hay archivos nuevos
      let backgroundImagePath = formData.background_image_path;
      if (backgroundImageFile) {
        backgroundImagePath = await uploadImage(backgroundImageFile, 'background');
        if (!backgroundImagePath) {
          setError('Error al subir imagen de fondo');
          return;
        }
      }

      let headerImagePath = formData.header_image_path;
      if (headerImageFile) {
        headerImagePath = await uploadImage(headerImageFile, 'header');
        if (!headerImagePath) {
          setError('Error al subir imagen de encabezado');
          return;
        }
      }

      // Preparar payload
      const payload: any = {
        ...formData,
        establishment_id: parseInt(establishmentId, 10),
        background_image_path: formData.background_type === 'image' ? backgroundImagePath : null,
        background_color: formData.background_type === 'color' ? formData.background_color : null,
        header_image_path: formData.header_type === 'image' ? headerImagePath : null,
        header_text: formData.header_type === 'text' ? formData.header_text : null,
        header_font_id: formData.header_type === 'text' ? formData.header_font_id : null,
        header_font_size: formData.header_type === 'text' ? formData.header_font_size : null,
        header_text_color: formData.header_type === 'text' ? formData.header_text_color : null,
        header_text_align: formData.header_type === 'text' ? formData.header_text_align : null,
        item_text_font_size: formData.item_text_font_size,
        item_price_font_size: formData.item_price_font_size,
        item_text_color: formData.item_text_color,
        item_price_color: formData.item_price_color,
        item_text_font_id: formData.item_text_font_id,
        item_price_font_id: formData.item_price_font_id,
        items: items.map((item, index) => ({
          ...item,
          display_order: item.display_order ?? index
        })),
        screens: selectedScreens.map((screen, index) => ({
          ...screen,
          display_order: screen.display_order ?? index
        }))
      };

      if (banner) {
        payload.id = banner.id;
      }

      const route = banner ? 'smart-banners/update' : 'smart-banners/create';
      const res = await apiFetch(
        route,
        {
          method: 'POST',
          body: JSON.stringify(payload)
        },
        token
      );

      if (isNetworkErrorResponse(res)) {
        setError('Error de conexión');
        return;
      }

      const json = await parseJsonResponse(res);

      if (json.error || !json.success) {
        const errorMessage = typeof json.error === 'string' ? json.error : json.message || `Error al ${banner ? 'actualizar' : 'crear'} el banner`;
        setError(errorMessage);
        return;
      }

      toast.success(`Banner inteligente ${banner ? 'actualizado' : 'creado'} exitosamente`);
      onSuccess();
    } catch (err: any) {
      console.error('Error guardando banner:', err);
      setError(err.message || 'Error al guardar banner');
    } finally {
      setLoading(false);
    }
  };

  const tabOptions = [
    { id: 'basic' as const, label: 'Básico' },
    { id: 'style' as const, label: 'Estilo' },
    { id: 'items' as const, label: 'Items' },
    { id: 'screens' as const, label: 'Pantallas' }
  ];

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <Alert variant="error">{error}</Alert>
      )}

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 border-b border-gray-200">
        {tabOptions.map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 text-sm font-medium rounded-t-md border-b-2 transition-colors ${
                isActive
                  ? 'text-red-600 border-red-600 bg-white'
                  : 'text-gray-500 border-transparent hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab: Básico */}
      {activeTab === 'basic' && (
        <div className="space-y-4">
          <Input
            label="Nombre del Banner"
            name="name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="Ej: Banner de Promociones"
            required
          />

          <Select
            label="Tipo de Banner"
            value={formData.banner_type}
            onChange={(e) => setFormData({ ...formData, banner_type: e.target.value as 'extras' | 'bebidas_postres' | 'chips' | 'freebies' | 'combo' | 'postres' })}
            options={[
              { value: 'extras', label: 'Extras' },
              { value: 'bebidas_postres', label: 'Bebidas y Postres' },
              { value: 'chips', label: 'Chips' },
              { value: 'freebies', label: 'Freebies' },
              { value: 'combo', label: 'Combo' },
              { value: 'postres', label: 'Postres' }
            ]}
          />

          <Select
            label="Columnas"
            value={formData.columns.toString()}
            onChange={(e) => setFormData({ ...formData, columns: parseInt(e.target.value, 10) })}
            options={[
              { value: '1', label: '1 Columna (Lista Vertical)' },
              { value: '2', label: '2 Columnas (Grid)' }
            ]}
          />

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Orden de Visualización"
              type="number"
              name="display_order"
              value={formData.display_order}
              onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value, 10) || 0 })}
              min="0"
            />

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Estado
              </label>
              <select
                value={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: parseInt(e.target.value, 10) })}
                className="w-full px-4 py-3 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                <option value={1}>Activo</option>
                <option value={0}>Inactivo</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Tab: Estilo */}
      {activeTab === 'style' && (
        <div className="space-y-6">
          {/* Configuración de Fondo */}
          <div>
            <h3 className="text-lg font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">
              Fondo
            </h3>
            <Select
              label="Tipo de Fondo"
              value={formData.background_type}
              onChange={(e) => {
                const newType = e.target.value as 'color' | 'image';
                setFormData({ ...formData, background_type: newType });
                if (newType === 'color' && !formData.background_color) {
                  setFormData(prev => ({ ...prev, background_color: '#ffffff' }));
                }
              }}
              options={[
                { value: 'color', label: 'Color Sólido' },
                { value: 'image', label: 'Imagen' }
              ]}
            />

            {formData.background_type === 'color' && (
              <div className="mt-4">
                <ColorPicker
                  label="Color de Fondo"
                  value={formData.background_color}
                  onChange={(e) => setFormData({ ...formData, background_color: e.target.value })}
                />
              </div>
            )}

            {formData.background_type === 'image' && (
              <div className="mt-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Imagen de Fondo
                </label>
                <input
                  type="file"
                  accept=".png,.jpg,.jpeg,.gif,.webp"
                  onChange={handleBackgroundImageChange}
                  className="block w-full text-sm text-gray-500
                    file:mr-4 file:py-2 file:px-4
                    file:rounded-lg file:border-0
                    file:text-sm file:font-semibold
                    file:bg-red-50 file:text-red-700
                    hover:file:bg-red-100
                    file:cursor-pointer"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Formatos permitidos: PNG, JPG, GIF, WEBP. Tamaño máximo: 5MB
                </p>
                {backgroundImagePreview && (
                  <div className="mt-4">
                    <img
                      src={backgroundImagePreview}
                      alt="Preview fondo"
                      className="max-w-full h-auto max-h-48 rounded-lg border border-gray-300"
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Configuración de Header */}
          <div>
            <h3 className="text-lg font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">
              Encabezado
            </h3>
            <Select
              label="Tipo de Encabezado"
              value={formData.header_type}
              onChange={(e) => {
                const newType = e.target.value as 'image' | 'text';
                setFormData({ ...formData, header_type: newType });
              }}
              options={[
                { value: 'image', label: 'Imagen' },
                { value: 'text', label: 'Texto Personalizado' }
              ]}
            />

            {formData.header_type === 'image' && (
              <div className="mt-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Imagen del Encabezado
                </label>
                <input
                  type="file"
                  accept=".png,.jpg,.jpeg,.gif,.webp"
                  onChange={handleHeaderImageChange}
                  className="block w-full text-sm text-gray-500
                    file:mr-4 file:py-2 file:px-4
                    file:rounded-lg file:border-0
                    file:text-sm file:font-semibold
                    file:bg-red-50 file:text-red-700
                    hover:file:bg-red-100
                    file:cursor-pointer"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Formatos permitidos: PNG, JPG, GIF, WEBP. Tamaño máximo: 5MB
                </p>
                {headerImagePreview && (
                  <div className="mt-4">
                    <img
                      src={headerImagePreview}
                      alt="Preview header"
                      className="max-w-full h-auto max-h-48 rounded-lg border border-gray-300"
                    />
                  </div>
                )}
              </div>
            )}

            {formData.header_type === 'text' && (
              <div className="mt-4 space-y-4">
                <Input
                  label="Texto del Encabezado"
                  name="header_text"
                  value={formData.header_text}
                  onChange={(e) => setFormData({ ...formData, header_text: e.target.value })}
                  placeholder="Ej: Promociones Especiales"
                  required={formData.header_type === 'text'}
                />

                <Select
                  label="Fuente"
                  value={formData.header_font_id?.toString() || ''}
                  onChange={(e) => setFormData({ ...formData, header_font_id: e.target.value ? parseInt(e.target.value, 10) : null })}
                  options={[
                    { value: '', label: '-- Sin fuente específica --' },
                    ...fonts.map(f => ({ value: f.id.toString(), label: f.display_name || f.name }))
                  ]}
                  disabled={loadingCatalogs}
                />

                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label="Tamaño de Fuente"
                    type="number"
                    name="header_font_size"
                    value={formData.header_font_size}
                    onChange={(e) => setFormData({ ...formData, header_font_size: parseInt(e.target.value, 10) || 24 })}
                    min="8"
                    max="72"
                  />

                  <Select
                    label="Alineación"
                    value={formData.header_text_align}
                    onChange={(e) => setFormData({ ...formData, header_text_align: e.target.value as 'left' | 'center' | 'right' })}
                    options={[
                      { value: 'left', label: 'Izquierda' },
                      { value: 'center', label: 'Centro' },
                      { value: 'right', label: 'Derecha' }
                    ]}
                  />
                </div>

                <ColorPicker
                  label="Color del Texto"
                  value={formData.header_text_color}
                  onChange={(e) => setFormData({ ...formData, header_text_color: e.target.value })}
                />
              </div>
            )}

            {/* Configuración de Textos de Items */}
            <div className="border-t border-gray-200 pt-6 mt-6">
              <h4 className="text-md font-semibold text-gray-800 mb-4">Configuración de Textos de Items</h4>
              <p className="text-sm text-gray-500 mb-4">
                Personaliza los tamaños, colores y fuentes para los textos de los items (productos/texto personalizado) dentro del banner.
              </p>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label="Tamaño de Fuente - Texto de Items"
                    type="number"
                    name="item_text_font_size"
                    value={formData.item_text_font_size}
                    onChange={(e) => setFormData({ ...formData, item_text_font_size: parseInt(e.target.value, 10) || 14 })}
                    min="8"
                    max="100"
                  />

                  <Input
                    label="Tamaño de Fuente - Precio de Items"
                    type="number"
                    name="item_price_font_size"
                    value={formData.item_price_font_size}
                    onChange={(e) => setFormData({ ...formData, item_price_font_size: parseInt(e.target.value, 10) || 16 })}
                    min="8"
                    max="100"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <ColorPicker
                    label="Color del Texto de Items"
                    value={formData.item_text_color}
                    onChange={(e) => setFormData({ ...formData, item_text_color: e.target.value })}
                  />

                  <ColorPicker
                    label="Color del Precio de Items"
                    value={formData.item_price_color}
                    onChange={(e) => setFormData({ ...formData, item_price_color: e.target.value })}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <Select
                    label="Fuente para Texto de Items"
                    value={formData.item_text_font_id?.toString() || ''}
                    onChange={(e) => setFormData({ ...formData, item_text_font_id: e.target.value ? parseInt(e.target.value, 10) : null })}
                    options={[
                      { value: '', label: 'Sin fuente personalizada (usar fuente de pantalla)' },
                      ...fonts.map(f => ({ value: f.id.toString(), label: f.display_name }))
                    ]}
                    disabled={loadingCatalogs}
                  />

                  <Select
                    label="Fuente para Precio de Items"
                    value={formData.item_price_font_id?.toString() || ''}
                    onChange={(e) => setFormData({ ...formData, item_price_font_id: e.target.value ? parseInt(e.target.value, 10) : null })}
                    options={[
                      { value: '', label: 'Sin fuente personalizada (usar fuente de pantalla)' },
                      ...fonts.map(f => ({ value: f.id.toString(), label: f.display_name }))
                    ]}
                    disabled={loadingCatalogs}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tab: Items */}
      {activeTab === 'items' && (
        <SmartBannerItemEditor
          items={items}
          onChange={setItems}
          availableReferences={availableReferences}
          availableComments={availableComments}
          loading={loadingCatalogs}
        />
      )}

      {/* Tab: Pantallas */}
      {activeTab === 'screens' && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Asignar a Pantallas
            </label>
            <p className="text-sm text-gray-500 mb-4">
              Selecciona las pantallas donde se mostrará este banner inteligente
            </p>
            
            {screens.length === 0 ? (
              <Alert variant="error">
                No hay pantallas disponibles para este establecimiento
              </Alert>
            ) : (
              <div className="space-y-3">
                {screens.map((screen) => {
                  const isSelected = selectedScreens.some(s => s.pantalla_id === screen.id);
                  const screenConfig = selectedScreens.find(s => s.pantalla_id === screen.id);
                  
                  return (
                    <div key={screen.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={(e) => {
                              if (e.target.checked) {
                                // Si es vertical, posición por defecto es 'inferior', si no 'superior'
                                const defaultPosition = screen.orientation === 'vertical' ? 'inferior' : 'superior';
                                
                                // Para pantallas verticales con posición inferior, asignar display_order 1 por defecto
                                // Si ya existe un banner con display_order 1, asignar 2
                                let defaultDisplayOrder = selectedScreens.length;
                                if (screen.orientation === 'vertical' && defaultPosition === 'inferior') {
                                  const existingOrder1 = selectedScreens.find(
                                    s => s.pantalla_id === screen.id && 
                                         s.position === 'inferior' && 
                                         s.display_order === 1
                                  );
                                  if (existingOrder1) {
                                    defaultDisplayOrder = 2;
                                  } else {
                                    defaultDisplayOrder = 1;
                                  }
                                }
                                
                                setSelectedScreens([...selectedScreens, {
                                  pantalla_id: screen.id,
                                  position: defaultPosition,
                                  display_order: defaultDisplayOrder
                                }]);
                              } else {
                                setSelectedScreens(selectedScreens.filter(s => s.pantalla_id !== screen.id));
                              }
                            }}
                            className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                          />
                          <span className="font-medium text-gray-900">{screen.name}</span>
                        </div>
                      </div>
                      
                      {isSelected && (
                        <div className="ml-7 space-y-3">
                          <Select
                            label="Posición"
                            value={screenConfig?.position || (screen.orientation === 'vertical' ? 'inferior' : 'superior')}
                            onChange={(e) => {
                              const newScreens = [...selectedScreens];
                              const index = newScreens.findIndex(s => s.pantalla_id === screen.id);
                              if (index >= 0) {
                                newScreens[index] = {
                                  ...newScreens[index],
                                  position: e.target.value as 'superior' | 'lateral' | 'inferior',
                                  lateral_position: e.target.value === 'lateral' ? (newScreens[index].lateral_position || 1) : null,
                                  // Resetear display_order si cambia de posición
                                  display_order: e.target.value === 'inferior' && screen.orientation === 'vertical' 
                                    ? (newScreens[index].display_order || 1)
                                    : (newScreens[index].display_order || 0)
                                };
                                setSelectedScreens(newScreens);
                              }
                            }}
                            options={
                              screen.orientation === 'vertical'
                                ? [{ value: 'inferior', label: 'Inferior' }]
                                : [
                                    { value: 'superior', label: 'Superior' },
                                    { value: 'lateral', label: 'Lateral' },
                                    { value: 'inferior', label: 'Inferior' }
                                  ]
                            }
                          />
                          
                          {/* Campo de orden de visualización para banners inferiores en pantallas verticales */}
                          {screenConfig?.position === 'inferior' && screen.orientation === 'vertical' && (
                            <Select
                              label="Orden de Visualización"
                              value={screenConfig.display_order?.toString() || '1'}
                              onChange={(e) => {
                                const newDisplayOrder = parseInt(e.target.value, 10);
                                
                                // Validar que no haya otro banner con el mismo display_order en la misma pantalla
                                const existingBannerWithOrder = selectedScreens.find(
                                  s => s.pantalla_id === screen.id && 
                                       s.position === 'inferior' && 
                                       s.display_order === newDisplayOrder &&
                                       s !== screenConfig
                                );
                                
                                if (existingBannerWithOrder) {
                                  alert(`Ya existe un banner con orden de visualización ${newDisplayOrder === 1 ? 'Izquierda' : 'Derecha'} para esta pantalla. Por favor, seleccione otro orden.`);
                                  return;
                                }
                                
                                const newScreens = [...selectedScreens];
                                const index = newScreens.findIndex(s => s.pantalla_id === screen.id);
                                if (index >= 0) {
                                  newScreens[index] = {
                                    ...newScreens[index],
                                    display_order: newDisplayOrder
                                  };
                                  setSelectedScreens(newScreens);
                                }
                              }}
                              options={[
                                { value: '1', label: 'Izquierda' },
                                { value: '2', label: 'Derecha' }
                              ]}
                            />
                          )}
                          
                          {screenConfig?.position === 'lateral' && (
                            <Select
                              label="Posición Lateral"
                              value={screenConfig.lateral_position?.toString() || '1'}
                              onChange={(e) => {
                                const newScreens = [...selectedScreens];
                                const index = newScreens.findIndex(s => s.pantalla_id === screen.id);
                                if (index >= 0) {
                                  newScreens[index] = {
                                    ...newScreens[index],
                                    lateral_position: parseInt(e.target.value, 10)
                                  };
                                  setSelectedScreens(newScreens);
                                }
                              }}
                              options={Array.from({ length: screen.banner_divisions || 3 }, (_, i) => ({
                                value: (i + 1).toString(),
                                label: `Lateral ${i + 1}`
                              }))}
                            />
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Botones de acción */}
      <div className="flex gap-2 pt-4 border-t border-gray-200">
        <Button
          type="submit"
          variant="primary"
          isLoading={loading || uploading}
          disabled={loading || uploading}
          className="flex-1"
        >
          {banner ? 'Actualizar Banner' : 'Crear Banner'}
        </Button>
        <Button
          type="button"
          variant="secondary"
          onClick={onCancel}
          className="flex-1"
        >
          Cancelar
        </Button>
      </div>
    </form>
  );
};

